import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
  HttpEvent,
} from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { ToastService } from '../shared/services/toast.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private toast: ToastService,
    private router: Router,
    private keycloak: KeycloakService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      map((event) => {
        if (event instanceof HttpResponse) {
          const msg = this.normalizeMessage(this.extractErrorMessage(event.body));
          if (msg) {
            this.toast.error(msg);

            const newBody = { ...event.body, __hasError: true };
            return event.clone({ body: newBody });
          }
        }
        return event;
      }),

      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          const keycloakInstance = this.keycloak.getKeycloakInstance();
          const tokenExpired = !keycloakInstance.token || keycloakInstance.isTokenExpired();

          if (tokenExpired) {
            this.toast.error('ERRORS.SESSION_EXPIRED');
            this.router.navigate(['/login']);
          } else {
            this.toast.error('ERRORS.UNAUTHORIZED_SERVICE');
            console.error('401 returned by backend service', {
              url: req.url,
              message: error.message,
            });
          }
        } else if (error.status === 403) {
          this.toast.error('ERRORS.ACCESS_DENIED');
        } else {
          const msg = this.normalizeMessage(this.extractErrorMessage(error?.error)) || 'ERRORS.SOMETHING_WENT_WRONG';
          this.toast.error(msg);
        }

        return throwError(() => error);
      }),
    );
  }

  // Backend still returns the old "Risk/FATCA" wording; display it as "KYC" (that step is now labeled KYC in the UI).
  private normalizeMessage(msg: string | null): string | null {
    if (!msg) return msg;

    if (/please fill the risk fac?ta questionnaire/i.test(msg)) {
      return 'ERRORS.COMPLETE_KYC_BEFORE_SUBMIT';
    }

    return msg.replace(/Risk\/FATCA/gi, 'KYC');
  }

  private extractErrorMessage(body: any): string | null {
    if (!body) return null;

    if (typeof body === 'string') {
      const isErrorText = /error|fail|exception|invalid|unauthorized|denied/i.test(body);
      return isErrorText ? body : null;
    }

    if (typeof body !== 'object') return null;

    if (body.error && typeof body.error === 'string') return body.error;
    if (body.errorMessage) return body.errorMessage;
    if (body.errMsg) return body.errMsg;

    if (body.message && typeof body.message === 'string' && body.success === false) {
      return body.message;
    }

    if (body.errors && Array.isArray(body.errors) && body.errors.length) {
      return body.errors.join(', ');
    }

    return null;
  }
}